import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import Button from '../components/common/Button';
// StatusBadge: kept imported to avoid a CRA fast-refresh edge case where an old ClubPage closure still referenced it after the badge UI was removed.
// eslint-disable-next-line no-unused-vars
import { BackButton, StatusBadge } from '../components/common';
import { Heading, Text, Typography, Caption } from '../components/common/Typography';
import { Card, StatsCard } from '../components/common/Card';
import SessionDetailsModal from '../components/sessions/SessionDetailsModal';
import CompletedSessionModal from '../components/sessions/CompletedSessionModal';
import PlayerDashboard from './PlayerDashboard';
import ClubTournamentList from '../components/tournaments/ClubTournamentList';
import Tabs from '../components/common/Tabs';
import {
  fetchClubLeaderboardsBundle,
  fetchPlayerSessionDatesForIds,
  viewerLadderTeaserFromBundle,
  LEADERBOARD_SCOPES,
} from '../services/clubLeaderboardService';
import {
  collectPlayerIdsFromRecentSnapshots,
  fetchRecentSnapshotsForScope,
} from '../services/leaderboardMovementService';
import { buildMovementMapForScope } from '../utils/leaderboardSnapshotUtils';
import { getScopeHelperText, parseLeaderboardScope } from '../utils/leaderboardPlayerScope';
import LeaderboardRow from '../components/leaderboard/LeaderboardRow';
import LeaderboardScopeToggle from '../components/leaderboard/LeaderboardScopeToggle';
import LeaderboardSectionSkeleton from '../components/leaderboard/LeaderboardSectionSkeleton';
import { ClubDashboardOverview } from '../components/clubs/dashboard/ClubDashboardOverview';
import { ClubScheduleTab } from '../components/clubs/dashboard/ClubScheduleTab';
import { ClubLeagueTab } from '../components/clubs/dashboard/ClubLeagueTab';
import { fetchClubLeagueLink } from '../services/leagueService';
import { APP_PAGE_BLEED_ROOT, APP_PAGE_GUTTER } from '../utils/sessionMobileLayout';
import InactiveClubMembershipView from '../components/clubs/InactiveClubMembershipView';
import { isInactiveMembership } from '../services/clubMembershipService';

/** Partnerships tab hidden until feature is updated */
const SHOW_CLUB_PARTNERSHIPS_TAB = false;

/** Single source for club dashboard nav tabs. */
const CLUB_NAV_TAB_META = [
  { id: 'overview',    label: 'My Club',       mobileLabel: 'My Club',  icon: '🏠', memberOnly: false },
  { id: 'leaderboard', label: 'Leaderboards', mobileLabel: 'Boards',   icon: '🏆', memberOnly: false, exploreAccent: 'info' },
  { id: 'club',        label: 'Club',          mobileLabel: 'Club',     icon: '🗓️', memberOnly: false, exploreAccent: 'primary' },
  { id: 'performance', label: 'Performance',  mobileLabel: 'Stats',    icon: '📊', memberOnly: true, exploreAccent: 'success' },
  { id: 'headtohead',  label: 'Matchups',     mobileLabel: 'Matchups', icon: '🏸', memberOnly: true, exploreAccent: 'warning' },
  { id: 'partnerships', label: 'Partnerships', mobileLabel: 'Partners', icon: '🤝', memberOnly: true, exploreAccent: 'elevated' },
];

const LEAGUE_NAV_TAB = { id: 'league', label: 'League', mobileLabel: 'League', icon: '🏟️', memberOnly: false, exploreAccent: 'info' };

/** Legacy tab IDs — redirect to their new equivalent. */
const LEGACY_TAB_REDIRECTS = { tournaments: 'club', 'club-info': 'club' };

const VISIBLE_CLUB_NAV_TABS = CLUB_NAV_TAB_META.filter(
  (t) => SHOW_CLUB_PARTNERSHIPS_TAB || t.id !== 'partnerships'
);

const ClubPage = ({ currentUser }) => {
  const { clubId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get navigation context from state
  const fromMyClubs = location.state?.fromMyClubs;
  const fromHomepage = location.state?.fromHomepage;
  const fromFindClub = location.state?.fromFindClub;
  
  const [club, setClub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Get tab from URL params, default to 'overview'
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const tabParam = searchParams.get('tab');
  const validTabs = useMemo(
    () => [...VISIBLE_CLUB_NAV_TABS.map((t) => t.id), LEAGUE_NAV_TAB.id],
    []
  );
  const initialTab = validTabs.includes(tabParam) ? tabParam : 'overview';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [userMembership, setUserMembership] = useState(null);
  const [leagueLink, setLeagueLink] = useState(undefined);
  const showManagementTab = useMemo(
    () => userMembership && ['owner', 'admin', 'host'].includes(userMembership.club_role),
    [userMembership]
  );
  const showLeagueTab = leagueLink?.status === 'approved';

  // Deep-link support for Matchups tab, e.g. from a session Insights "Matchups" link
  const opponentIdFromUrl = searchParams.get('opponentId') || null;

  const handleTabChange = useCallback(
    (tabId) => {
      setActiveTab(tabId);
      const newParams = new URLSearchParams(location.search);
      newParams.set('tab', tabId);
      navigate(`${location.pathname}?${newParams.toString()}`, { replace: true });
    },
    [location.pathname, location.search, navigate]
  );
  const handleViewLeague = useCallback(() => handleTabChange('league'), [handleTabChange]);

  // Update activeTab when URL param changes
  useEffect(() => {
    if (tabParam && validTabs.includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [tabParam, validTabs]);

  // Hidden or member-only tabs redirect to overview (deep links)
  useEffect(() => {
    // Redirect legacy tab IDs to their new equivalents first
    if (LEGACY_TAB_REDIRECTS[activeTab]) {
      const target = LEGACY_TAB_REDIRECTS[activeTab];
      setActiveTab(target);
      const newParams = new URLSearchParams(location.search);
      newParams.set('tab', target);
      navigate(`${location.pathname}?${newParams.toString()}`, { replace: true });
      return;
    }
    const shouldRedirect =
      activeTab === 'partnerships' ||
      (!currentUser && ['performance', 'headtohead'].includes(activeTab)) ||
      (activeTab === 'league' && !showLeagueTab && leagueLink !== undefined);
    if (shouldRedirect) {
      setActiveTab('overview');
      const newParams = new URLSearchParams(location.search);
      newParams.set('tab', 'overview');
      navigate(`${location.pathname}?${newParams.toString()}`, { replace: true });
    }
  }, [currentUser, activeTab, location.pathname, location.search, navigate, showLeagueTab, leagueLink]);

  // Data states
  const [leaderboard, setLeaderboard] = useState({ 
    levelsMale: [], 
    levelsFemale: [], 
    mixed: [],
    combined: [],
    userPositions: {
      levelsMale: null,
      levelsFemale: null,
      mixed: null,
      combined: null,
    }
  });
  const [leaderboardScope, setLeaderboardScope] = useState(() =>
    parseLeaderboardScope(searchParams.get('scope'))
  );
  // Movement deltas from latest snapshot per ladder — rank_delta / elo_delta only.
  // Keyed as { mixed: Map, levelsMale: Map, levelsFemale: Map, combined: Map }.
  // Live rank # is always from PCR; snapshot.rank never shown on list rows.
  const [snapshotMovement, setSnapshotMovement] = useState({
    mixed: new Map(),
    levelsMale: new Map(),
    levelsFemale: new Map(),
    combined: new Map(),
  });
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  // Overview recap: live Active ranks for the current user (same source as the leaderboard tab).
  const [overviewRecap, setOverviewRecap] = useState({ mixed: null, levels: null });
  const [overviewRecapLoading, setOverviewRecapLoading] = useState(false);
  const [seasonSessionsPlayed, setSeasonSessionsPlayed] = useState(null);
  const leaderboardRequestRef = useRef(0);
  // Optional "biggest mover" from the mixed ladder latest snapshot.
  const [biggestMover, setBiggestMover] = useState(null);
  const [recentSessions, setRecentSessions] = useState([]);
  const [upcomingSessions, setUpcomingSessions] = useState([]);
  const [clubStats, setClubStats] = useState(null);

  const getRoleDisplayName = (role) => {
    switch (role) {
      case 'owner':
        return 'Owner';
      case 'admin':
        return 'Admin';
      case 'host':
        return 'Host';
      case 'member':
        return 'Member';
      default:
        return role || 'Member';
    }
  };

  const handleOpenManagement = useCallback(() => {
    if (!club?.id) return;
    navigate(`/club-management/${club.id}`, {
      state: {
        selectedClub: club,
        userRole: userMembership?.club_role,
        fromMyClubs: !!fromMyClubs,
        fromHomepage: !!fromHomepage,
        returnTab: activeTab,
      },
    });
  }, [club, userMembership, fromMyClubs, fromHomepage, activeTab, navigate]);

  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showCompletedSessionModal, setShowCompletedSessionModal] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState(null);

  useEffect(() => {
    if (clubId) {
      fetchClubData();
    }
  }, [clubId, currentUser]);

  const LEADERBOARD_DATA_TABS = useMemo(() => ['overview', 'leaderboard'], []);

  useEffect(() => {
    setSeasonSessionsPlayed(null);
  }, [clubId, selectedSeason?.id]);

  useEffect(() => {
    if (
      selectedSeason &&
      clubId &&
      LEADERBOARD_DATA_TABS.includes(activeTab)
    ) {
      fetchLeaderboardData();
    }
  }, [selectedSeason, clubId, leaderboardScope, activeTab]);

  useEffect(() => {
    setLeaderboardScope(parseLeaderboardScope(searchParams.get('scope')));
  }, [searchParams]);

  const handleLeaderboardScopeChange = useCallback(
    (nextScope) => {
      setLeaderboardScope(nextScope);
      const newParams = new URLSearchParams(location.search);
      newParams.set('tab', 'leaderboard');
      if (nextScope === LEADERBOARD_SCOPES.ACTIVE) {
        newParams.delete('scope');
      } else {
        newParams.set('scope', nextScope);
      }
      navigate(`${location.pathname}?${newParams.toString()}`, { replace: true });
    },
    [location.pathname, location.search, navigate]
  );

  const fetchLeaderboardData = async () => {
    if (!selectedSeason) return;

    const requestId = ++leaderboardRequestRef.current;
    setLeaderboardLoading(true);
    if (leaderboardScope === LEADERBOARD_SCOPES.ACTIVE) {
      setOverviewRecapLoading(true);
    }

    try {
      const bundle = await fetchClubLeaderboardsBundle({
        clubId,
        seasonId: selectedSeason.id,
        scope: leaderboardScope,
        currentUserId: currentUser?.id,
      });

      setLeaderboard(bundle.leaderboard);
      setLeaderboardLoading(false);
      if (currentUser?.id && requestId === leaderboardRequestRef.current) {
        const played = bundle.sessionSummaries?.[currentUser.id]?.sessionsPlayed;
        setSeasonSessionsPlayed(typeof played === 'number' ? played : 0);
      }

      const ladderConfigs = [
        { key: 'mixed', ladderType: 'mixed' },
        { key: 'levelsMale', ladderType: 'levels_male' },
        { key: 'levelsFemale', ladderType: 'levels_female' },
        { key: 'combined', ladderType: 'combined' },
      ];

      const recentSnapshotsList = await Promise.all(
        ladderConfigs.map(({ ladderType }) =>
          fetchRecentSnapshotsForScope(
            clubId,
            selectedSeason.id,
            ladderType,
            leaderboardScope
          ).catch(() => null)
        )
      );

      let sessionDatesByPlayerId = {};
      if (leaderboardScope === LEADERBOARD_SCOPES.ACTIVE) {
        const snapshotPlayerIds = collectPlayerIdsFromRecentSnapshots(recentSnapshotsList);
        if (snapshotPlayerIds.length > 0) {
          sessionDatesByPlayerId = await fetchPlayerSessionDatesForIds(
            snapshotPlayerIds,
            clubId,
            selectedSeason.id
          );
        }
      }

      const movementState = {};
      ladderConfigs.forEach(({ key }, index) => {
        movementState[key] = buildMovementMapForScope(
          leaderboardScope,
          recentSnapshotsList[index],
          sessionDatesByPlayerId
        );
      });

      setSnapshotMovement(movementState);

      if (leaderboardScope === LEADERBOARD_SCOPES.ACTIVE && currentUser?.id) {
        setOverviewRecap(
          viewerLadderTeaserFromBundle({
            clubId,
            seasonId: selectedSeason.id,
            viewerPlayerId: currentUser.id,
            viewerGender: currentUser.gender ?? null,
            userPositions: bundle.leaderboard.userPositions,
            fullRanked: bundle.leaderboard.fullRanked,
            movementByLadder: movementState,
          })
        );
        setOverviewRecapLoading(false);
      }

      const mixedRecent = recentSnapshotsList[ladderConfigs.findIndex((l) => l.key === 'mixed')];
      const mixedEntries = mixedRecent?.current?.entries ?? [];
      const biggestMoverEntry = mixedEntries.reduce((best, row) => {
        if (row.rank_delta === null || row.rank_delta === undefined) return best;
        if (!best || row.rank_delta > best.rank_delta) return row;
        return best;
      }, null);
      setBiggestMover(biggestMoverEntry && biggestMoverEntry.rank_delta > 0 ? biggestMoverEntry : null);
    } catch (err) {
      console.error('Error fetching leaderboard data:', err);
      setLeaderboardLoading(false);
      setOverviewRecapLoading(false);
    }
  };

  const fetchClubData = async () => {
    if (!currentUser?.id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Check if user is a member of this club
      const { data: membership, error: membershipError } = await supabase
        .from('players_club')
        .select('*')
        .eq('club_id', clubId)
        .eq('player_id', currentUser.id)
        .maybeSingle();

      if (membershipError || !membership || membership.club_role === 'guest') {
        setError('You are not a member of this club');
        return;
      }

      setUserMembership(membership);

      if (isInactiveMembership(membership)) {
        const { data: clubData, error: clubError } = await supabase
          .from('clubs')
          .select('*')
          .eq('id', clubId)
          .single();
        if (clubError) throw clubError;
        setClub(clubData);
        return;
      }

      if (!membership.is_active) {
        setError('You are not a member of this club');
        return;
      }
      fetchClubLeagueLink(clubId)
        .then(setLeagueLink)
        .catch(() => setLeagueLink(null));

      // Fetch all club data in parallel
      const [
        { data: clubData, error: clubError },
        { data: recentSessionsData, error: recentError },
        { data: upcomingSessionsData, error: upcomingError },
        { data: statsData, error: statsError },
        { data: seasonsData, error: seasonsError },
        { data: membersData, error: membersError }
      ] = await Promise.all([
        // Club details
        supabase
          .from('clubs')
          .select('*')
          .eq('id', clubId)
          .single(),
        
      // Recent sessions (last 5) - use same pattern as existing code
      supabase
        .from('sessions')
        .select(`
          *,
          session_types:session_type_id ( id, name ),
          session_signups ( id, player_id ),
          session_games ( id ),
          session_players ( player_id )
        `)
        .eq('club_id', clubId)
        .in('session_status', ['completed', 'archived'])
        .order('session_date', { ascending: false })
        .limit(5),
      
      // Upcoming sessions — live only (upcoming/ongoing), not draft
      supabase
        .from('sessions')
        .select(`
          *,
          session_types:session_type_id ( id, name ),
          session_signups (
            player_id,
            signup_status,
            players:player_id (
              first_name,
              last_name
            )
          )
        `)
        .eq('club_id', clubId)
        .in('session_status', ['upcoming', 'ongoing'])
        .gte('session_date', new Date().toISOString())
        .order('session_date', { ascending: true })
        .limit(5),
      
      // Club statistics
      supabase
        .from('sessions')
        .select('id, session_status, session_date, created_at')
        .eq('club_id', clubId),
      
      // Seasons data
      supabase
        .from('seasons')
        .select('id, name, start_date, end_date, season_status')
        .eq('club_id', clubId)
        .order('start_date', { ascending: false }),
      
      // Member count - exclude archived guests (is_guest = true AND is_active = false)
      supabase
        .from('players_club')
        .select(`
          id,
          players:player_id (
            id,
            is_guest,
            is_active,
            merged_at,
            merged_into_user_id
          )
        `, { count: 'exact' })
        .eq('club_id', clubId)
        .eq('is_active', true)
      ]);

      if (clubError) throw clubError;
      if (recentError) throw recentError;
      if (upcomingError) throw upcomingError;
      if (statsError) throw statsError;
      if (membersError) throw membersError;

      setClub(clubData);
      setRecentSessions(recentSessionsData || []);
      setUpcomingSessions(upcomingSessionsData || []);
      
      // Unique club players: active memberships only; drop inactive/deleted players,
      // archived guests, and merged (linked) guest rows so they are not double-counted with a member account
      const countsTowardClubPlayers = (member) => {
        const player = member?.players;
        if (!player?.id) return false;
        if (player.is_active === false) return false;
        if (player.is_guest === true && player.is_active === false) return false;
        if (
          player.is_guest === true &&
          (player.merged_at != null || player.merged_into_user_id != null)
        ) {
          return false;
        }
        return true;
      };
      const clubPlayerIds = new Set();
      (membersData || []).forEach((member) => {
        if (countsTowardClubPlayers(member)) {
          clubPlayerIds.add(member.players.id);
        }
      });
      const clubPlayersCount = clubPlayerIds.size;
      
      // Calculate club stats
      const totalSessions = statsData?.length || 0;
      const finishedSessions = statsData?.filter(s => ['completed', 'archived'].includes(s.session_status)).length || 0;
      // Count upcoming sessions using same logic as getUpcomingSessions
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      const upcomingSessionsCount = statsData?.filter(s => {
        if (!s.session_date) return false;
        const sessionDate = new Date(s.session_date);
        return sessionDate >= now && ['upcoming', 'ongoing'].includes(s.session_status);
      }).length || 0;
      
      setClubStats({
        totalSessions,
        finishedSessions,
        upcomingSessionsCount,
        clubPlayersCount,
        seasonsCount: seasonsData?.length || 0
      });
      
      // Set seasons and default selected season (honour ?season=<uuid> deep links from Home, etc.)
      setSeasons(seasonsData || []);
      if (seasonsData && seasonsData.length > 0) {
        const urlSeasonId = new URLSearchParams(location.search).get('season');
        const match = urlSeasonId
          ? seasonsData.find((s) => s.id === urlSeasonId)
          : null;
        setSelectedSeason(match ?? seasonsData[0]);
      }

    } catch (err) {
      console.error('Error fetching club data:', err);
      setError('Failed to load club data');
    } finally {
      setLoading(false);
    }
  };

  const handleSignupToSession = async (sessionId) => {
    try {
      const { error } = await supabase
        .from('session_signups')
        .insert({
          session_id: sessionId,
          player_id: currentUser.id,
          signup_status: 'confirmed',
          added_by: currentUser.id
        });

      if (error) throw error;
      
      // Refresh upcoming sessions
      fetchClubData();
    } catch (err) {
      console.error('Error signing up for session:', err);
      setError('Failed to sign up for session');
    }
  };

  const handleCancelSignup = async (sessionId) => {
    try {
      const { error } = await supabase
        .from('session_signups')
        .delete()
        .eq('session_id', sessionId)
        .eq('player_id', currentUser.id);

      if (error) throw error;

      fetchClubData();
    } catch (err) {
      console.error('Error canceling signup for session:', err);
      setError('Failed to cancel session signup');
    }
  };

  const handleViewSessionDetails = (sessionId) => {
    setSelectedSessionId(sessionId);
    setShowDetailsModal(true);
  };

  const handleViewCompletedSession = (sessionId) => {
    setSelectedSessionId(sessionId);
    setShowCompletedSessionModal(true);
  };

  const handleViewSessionPerformance = useCallback(
    (sessionId) => {
      if (!currentUser?.id || !sessionId) return;
      navigate(`/player/${currentUser.id}/session/${sessionId}`, { state: { clubId } });
    },
    [currentUser?.id, clubId, navigate]
  );

  const handleCloseSessionDetails = () => {
    setShowDetailsModal(false);
    setSelectedSessionId(null);
  };

  const handleCloseCompletedSession = () => {
    setShowCompletedSessionModal(false);
    setSelectedSessionId(null);
  };

  const desktopTabs = useMemo(
    () =>
      [
        ...VISIBLE_CLUB_NAV_TABS.filter((t) => !t.memberOnly || currentUser),
        ...(showLeagueTab ? [LEAGUE_NAV_TAB] : []),
      ].map((t) => ({
        id: t.id,
        label: t.label,
        mobileLabel: t.mobileLabel,
        icon: t.icon,
        content: null,
      })),
    [currentUser, showLeagueTab]
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <Typography variant="muted">Loading club data...</Typography>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card variant="default" size="large" className="text-center">
          <div className="text-red-500 text-6xl mb-6">⚠️</div>
          <Heading level={2} className="mb-4">Access Denied</Heading>
          <Text className="mb-8">{error}</Text>
            <Button
              onClick={() => navigate('/my-clubs')}
              variant="primary"
            >
              Go to My Clubs
            </Button>
        </Card>
      </div>
    );
  }

  if (!club) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card variant="default" size="large" className="text-center">
          <div className="text-text-muted text-6xl mb-6">🏸</div>
          <Heading level={2} className="mb-4">Club Not Found</Heading>
          <Text className="mb-8">The club you're looking for doesn't exist.</Text>
            <Button
              onClick={() => navigate('/my-clubs')}
              variant="primary"
            >
              Go to My Clubs
            </Button>
        </Card>
      </div>
    );
  }

  if (isInactiveMembership(userMembership)) {
    const isPublicClubsRoute = window.location.pathname.startsWith('/clubs/');
    const inactiveBackLabel = fromMyClubs
      ? 'Back to My Clubs'
      : isPublicClubsRoute
        ? 'Back to Clubs'
        : fromHomepage
          ? 'Back to Home'
          : 'Back to Clubs';
    const handleInactiveBack = () => {
      if (fromMyClubs) navigate('/profile/clubs');
      else if (isPublicClubsRoute) navigate('/clubs');
      else if (fromHomepage) navigate('/');
      else navigate('/clubs');
    };

    return (
      <div className={`min-h-screen bg-background ${APP_PAGE_BLEED_ROOT}`}>
        <div className={`max-w-app-shell mx-auto ${APP_PAGE_GUTTER} pt-3 md:pt-6 pb-8`}>
          <InactiveClubMembershipView
            club={club}
            clubId={clubId}
            currentUser={currentUser}
            membership={userMembership}
            onBack={handleInactiveBack}
            backLabel={inactiveBackLabel}
            onMembershipChange={(patch) =>
              setUserMembership((prev) => (prev ? { ...prev, ...patch } : prev))
            }
          />
        </div>
      </div>
    );
  }

  const handleBack = () => {
    const isPublicClubsRoute = window.location.pathname.startsWith('/clubs/');

    if (fromMyClubs) {
      navigate('/profile/clubs');
    } else if (isPublicClubsRoute) {
      navigate('/clubs');
    } else if (fromHomepage) {
      navigate('/');
    } else if (fromFindClub) {
      navigate('/my-clubs');
    } else {
      navigate('/clubs');
    }
  };

  const isPublicClubsRoute = window.location.pathname.startsWith('/clubs/');
  const backLabel =
    fromMyClubs
      ? 'Back to My Clubs'
      : isPublicClubsRoute
        ? 'Back to Clubs'
        : fromHomepage
          ? 'Back to Home'
          : fromFindClub
            ? 'Back to My Clubs'
            : 'Back to Clubs';

  return (
    <div className={`min-h-screen bg-background ${APP_PAGE_BLEED_ROOT}`}>
      <div className={`max-w-app-shell mx-auto ${APP_PAGE_GUTTER} pt-3 md:pt-6 pb-8`}>
        {/* Back — one button: mobile sub-tabs return to overview; otherwise leave the club */}
        <div className="mb-3 md:mb-0 flex items-center justify-between">
          {activeTab !== 'overview' ? (
            <BackButton
              onClick={() => handleTabChange('overview')}
              className="md:hidden"
            >
              Back to My Club
            </BackButton>
          ) : null}
          <BackButton
            onClick={handleBack}
            className={activeTab !== 'overview' ? 'hidden md:inline-flex' : ''}
          >
            {backLabel}
          </BackButton>
          {/* For public route, show Join/Request Access button if not a member */}
          {isPublicClubsRoute && !userMembership && (
            <Button
              variant="primary"
              onClick={() => {
                // TODO: Implement join request modal or navigation
                console.log('Join club clicked');
              }}
            >
              Request to Join
            </Button>
          )}
        </div>

        {/* Navigation Tabs — desktop only */}
        <div className="mt-4 hidden md:block">
          <Tabs tabs={desktopTabs} activeTab={activeTab} onTabChange={handleTabChange} />
        </div>

        <div className="mt-8">
        {activeTab === 'overview' && (
          <ClubDashboardOverview
            club={club}
            clubId={clubId}
            clubStats={clubStats}
            currentUser={currentUser}
            canManage={showManagementTab}
            selectedSeason={selectedSeason}
            recentSessions={recentSessions}
            upcomingSessions={upcomingSessions}
            leaderboard={leaderboard}
            snapshotMovement={snapshotMovement}
            leaderboardLoading={leaderboardLoading}
            sessionsPlayed={seasonSessionsPlayed}
            overviewRecap={overviewRecap}
            overviewRecapLoading={overviewRecapLoading}
            onViewSessionPerformance={handleViewSessionPerformance}
            onViewLeaderboard={() => {
              const qs = new URLSearchParams({ tab: 'leaderboard' });
              if (selectedSeason?.id) qs.set('season', selectedSeason.id);
              navigate(`${location.pathname}?${qs.toString()}`);
            }}
            onViewStats={() => handleTabChange('performance')}
            onViewMatchups={() => handleTabChange('headtohead')}
            onViewLeague={leagueLink?.status === 'approved' ? handleViewLeague : undefined}
            onOpenManagement={handleOpenManagement}
            onRefresh={fetchClubData}
          />
        )}

        {activeTab === 'club' && (
          <ClubScheduleTab
            club={club}
            clubStats={clubStats}
            userMembership={userMembership}
            getRoleDisplayName={getRoleDisplayName}
            recentSessions={recentSessions}
            upcomingSessions={upcomingSessions}
            currentUser={currentUser}
            onViewSessionDetails={handleViewSessionDetails}
            onViewSessionPerformance={handleViewSessionPerformance}
            clubId={clubId}
            canManage={showManagementTab}
            onRefresh={fetchClubData}
            selectedSeason={selectedSeason}
            seasons={seasons}
            setSelectedSeason={setSelectedSeason}
          />
        )}

        {activeTab === 'leaderboard' && (
          <div>
            <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <label htmlFor="season-select" className="block text-sm font-medium text-text-secondary mb-2">
                  Select Season
                </label>
                <select
                  id="season-select"
                  value={selectedSeason?.id || ''}
                  onChange={(e) => {
                    const season = seasons.find(s => s.id === e.target.value);
                    setSelectedSeason(season);
                  }}
                  className="form-input max-w-xs"
                >
                  {seasons.map((season) => (
                    <option key={season.id} value={season.id}>
                      {season.name} ({new Date(season.start_date).getFullYear()})
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-2 sm:items-end">
                <LeaderboardScopeToggle
                  scope={leaderboardScope}
                  onScopeChange={handleLeaderboardScopeChange}
                />
                <Caption className="text-xs">
                  {getScopeHelperText(leaderboardScope)}
                </Caption>
              </div>
            </div>

            {selectedSeason ? (
              leaderboardLoading ? (
                <div className="space-y-4 sm:space-y-8">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-8">
                    <LeaderboardSectionSkeleton rowCount={5} />
                    <LeaderboardSectionSkeleton rowCount={5} />
                  </div>
                  <LeaderboardSectionSkeleton rowCount={8} />
                  <LeaderboardSectionSkeleton rowCount={8} />
                </div>
              ) : (
              <div className="space-y-4 sm:space-y-8">
                {/* Levels Leaderboards - Men's and Women's */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-8">
                  {/* Men's Doubles Leaderboard */}
                  <div className="bg-surface rounded-xl shadow-sm border border-border p-3 sm:p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-text-primary">
                      Men's Doubles - {selectedSeason.name}
                    </h3>
                      <button
                        onClick={() => navigate(`/clubs/${clubId}/leaderboard/levels-male?season=${selectedSeason.id}`)}
                        className="flex items-center gap-1 text-sm text-primary hover:text-primary-dark font-medium"
                      >
                        View all
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                    {leaderboard.levelsMale.length > 0 ? (
                      <div className="space-y-2 sm:space-y-3">
                        {leaderboard.levelsMale.map((player, index) => {
                          const isUser = player.players?.id === currentUser?.id;
                          const userPosition = leaderboard.userPositions?.levelsMale;
                          const isInTop5 = isUser && userPosition && userPosition <= 5;
                          const isOutsideTop5 = isUser && userPosition && userPosition > 5;
                          const showSeparator = index === 5 && isOutsideTop5;
                          const actualPosition = isUser && userPosition ? userPosition : index + 1;
                          const movement = snapshotMovement.levelsMale.get(player.players?.id);
                          
                          return (
                            <React.Fragment key={`levels-male-${player.players?.id || index}`}>
                              {showSeparator && (
                                <div className="flex items-center justify-center py-2">
                                  <div className="flex-1 border-t border-border"></div>
                                  <span className="px-4 text-sm text-text-secondary">...</span>
                                  <div className="flex-1 border-t border-border"></div>
                                </div>
                              )}
                              <LeaderboardRow
                                rank={actualPosition}
                                player={player.players}
                                elo={player.levels_elo_rating}
                                isCurrentUser={isUser}
                                rankDelta={movement?.rank_delta ?? null}
                                highlight={isUser ? (isInTop5 ? 'primary' : 'accent') : 'none'}
                                rankTierMode="preview"
                              />
                            </React.Fragment>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-text-muted text-center py-4">No men's doubles data available</p>
                    )}
                  </div>

                  {/* Women's Doubles Leaderboard */}
                  <div className="bg-surface rounded-xl shadow-sm border border-border p-3 sm:p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-text-primary">
                      Women's Doubles - {selectedSeason.name}
                    </h3>
                      <button
                        onClick={() => navigate(`/clubs/${clubId}/leaderboard/levels-female?season=${selectedSeason.id}`)}
                        className="flex items-center gap-1 text-sm text-primary hover:text-primary-dark font-medium"
                      >
                        View all
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                    {leaderboard.levelsFemale.length > 0 ? (
                      <div className="space-y-2 sm:space-y-3">
                        {leaderboard.levelsFemale.map((player, index) => {
                          const isUser = player.players?.id === currentUser?.id;
                          const userPosition = leaderboard.userPositions?.levelsFemale;
                          const isInTop5 = isUser && userPosition && userPosition <= 5;
                          const isOutsideTop5 = isUser && userPosition && userPosition > 5;
                          const showSeparator = index === 5 && isOutsideTop5;
                          const actualPosition = isUser && userPosition ? userPosition : index + 1;
                          const movement = snapshotMovement.levelsFemale.get(player.players?.id);
                          
                          return (
                            <React.Fragment key={`levels-female-${player.players?.id || index}`}>
                              {showSeparator && (
                                <div className="flex items-center justify-center py-2">
                                  <div className="flex-1 border-t border-border"></div>
                                  <span className="px-4 text-sm text-text-secondary">...</span>
                                  <div className="flex-1 border-t border-border"></div>
                                </div>
                              )}
                              <LeaderboardRow
                                rank={actualPosition}
                                player={player.players}
                                elo={player.levels_elo_rating}
                                isCurrentUser={isUser}
                                rankDelta={movement?.rank_delta ?? null}
                                highlight={isUser ? (isInTop5 ? 'primary' : 'accent') : 'none'}
                                rankTierMode="preview"
                              />
                            </React.Fragment>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-text-muted text-center py-4">No women's doubles data available</p>
                    )}
                  </div>
                </div>

                {/* Mixed Doubles Leaderboard */}
                <div className="bg-surface rounded-xl shadow-sm border border-border p-3 sm:p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-text-primary">
                    Mixed Doubles Leaderboard - {selectedSeason.name}
                  </h3>
                    <button
                      onClick={() => navigate(`/clubs/${clubId}/leaderboard/mixed?season=${selectedSeason.id}`)}
                      className="flex items-center gap-1 text-sm text-primary hover:text-primary-dark font-medium"
                    >
                      View all
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                    {leaderboard.mixed.length > 0 ? (
                    <div className="space-y-2 sm:space-y-3">
                      {leaderboard.mixed.map((player, index) => {
                        const isUser = player.players?.id === currentUser?.id;
                        const userPosition = leaderboard.userPositions?.mixed;
                        const isInTop10 = isUser && userPosition && userPosition <= 10;
                        const isOutsideTop10 = isUser && userPosition && userPosition > 10;
                        // Show separator before the user's entry when they're outside top 10
                        // If we have more than 10 items, index 10 is the user's entry (outside top 10)
                        const showSeparator = leaderboard.mixed.length > 10 && index === 10 && userPosition && userPosition > 10;
                        const actualPosition = isUser && userPosition ? userPosition : index + 1;
                        const movement = snapshotMovement.mixed.get(player.players?.id);
                        
                        return (
                          <React.Fragment key={`mixed-${player.players?.id || index}`}>
                            {showSeparator && (
                              <div className="flex items-center justify-center py-2">
                                <div className="flex-1 border-t border-border"></div>
                                <span className="px-4 text-sm text-text-secondary">...</span>
                                <div className="flex-1 border-t border-border"></div>
                              </div>
                            )}
                            <LeaderboardRow
                              rank={actualPosition}
                              player={player.players}
                              elo={player.mixed_elo_rating}
                              isCurrentUser={isUser}
                              rankDelta={movement?.rank_delta ?? null}
                              highlight={isUser ? (isInTop10 ? 'primary' : 'accent') : 'none'}
                              rankTierMode="preview"
                            />
                          </React.Fragment>
                        );
                      })}
                    </div>
                    ) : (
                    <p className="text-text-muted text-center py-4">No mixed doubles data available</p>
                  )}
                </div>

                {/* Combined Leaderboard */}
                <div className="bg-surface rounded-xl shadow-sm border border-border p-3 sm:p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-text-primary">
                      Combined Leaderboard - {selectedSeason.name}
                    </h3>
                    <button
                      onClick={() => {
                        const qs = new URLSearchParams({ season: selectedSeason.id });
                        if (leaderboardScope === LEADERBOARD_SCOPES.ALL) qs.set('scope', 'all');
                        navigate(`/clubs/${clubId}/leaderboard/combined?${qs.toString()}`);
                      }}
                      className="flex items-center gap-1 text-sm text-primary hover:text-primary-dark font-medium"
                    >
                      View all
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                  {leaderboard.combined?.length > 0 ? (
                    <div className="space-y-2 sm:space-y-3">
                      {leaderboard.combined.map((player, index) => {
                        const isUser = player.players?.id === currentUser?.id;
                        const userPosition = leaderboard.userPositions?.combined;
                        const isInTop10 = isUser && userPosition && userPosition <= 10;
                        const isOutsideTop10 = isUser && userPosition && userPosition > 10;
                        const showSeparator = leaderboard.combined.length > 10 && index === 10 && userPosition && userPosition > 10;
                        const actualPosition = isUser && userPosition ? userPosition : index + 1;
                        const movement = snapshotMovement.combined?.get(player.players?.id);

                        return (
                          <React.Fragment key={`combined-${player.players?.id || index}`}>
                            {showSeparator && (
                              <div className="flex items-center justify-center py-2">
                                <div className="flex-1 border-t border-border"></div>
                                <span className="px-4 text-sm text-text-secondary">...</span>
                                <div className="flex-1 border-t border-border"></div>
                              </div>
                            )}
                            <LeaderboardRow
                              rank={actualPosition}
                              player={player.players}
                              elo={player.combined_elo_rating}
                              isCurrentUser={isUser}
                              rankDelta={movement?.rank_delta ?? null}
                              highlight={isUser ? (isInTop10 ? 'primary' : 'accent') : 'none'}
                              rankTierMode="preview"
                            />
                          </React.Fragment>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-text-muted text-center py-4">No combined leaderboard data available</p>
                  )}
                </div>
              </div>
              )
            ) : (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">🏆</div>
                <h3 className="text-2xl font-semibold text-text-primary mb-4">No Season Selected</h3>
                <p className="text-text-secondary mb-6">
                  Please select a season to view the leaderboard.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Performance Tab */}
        {activeTab === 'performance' && currentUser && (
          <div>
            {/* Season Filter */}
            <div className="mb-6">
              <label htmlFor="performance-season-select" className="block text-sm font-medium text-text-secondary mb-2">
                Select Season
              </label>
              <select
                id="performance-season-select"
                value={selectedSeason?.id || ''}
                onChange={(e) => {
                  const season = seasons.find(s => s.id === e.target.value);
                  setSelectedSeason(season);
                }}
                className="form-input max-w-xs"
              >
                <option value="">All Seasons</option>
                {seasons.map((season) => (
                  <option key={season.id} value={season.id}>
                    {season.name} ({new Date(season.start_date).getFullYear()})
                  </option>
                ))}
              </select>
            </div>

            {/* Performance Content - no white container, sections have their own cards */}
            <PlayerDashboard 
              currentUser={currentUser} 
              embedded={true} 
              activeView="summary"
              forceClubId={clubId}
            />
          </div>
        )}

        {/* Matchups Tab */}
        {activeTab === 'league' && showLeagueTab && (
          <ClubLeagueTab
            clubId={clubId}
            canManage={showManagementTab}
            currentUserId={currentUser?.id}
            viewerGender={currentUser?.gender ?? null}
          />
        )}

        {activeTab === 'headtohead' && currentUser && (
          <div>
            <div className="mb-4 md:mb-6">
              <Heading level={2} className="text-xl sm:text-2xl mb-1">
                Matchups
              </Heading>
              <Text className="text-sm text-text-secondary">
                Compare your record with teammates and opponents at this club.
              </Text>
            </div>
            <PlayerDashboard
              currentUser={currentUser}
              embedded={true}
              activeView="headtohead"
              forceClubId={clubId}
              initialOpponentId={opponentIdFromUrl}
            />
          </div>
        )}

        {/* Partnerships Tab — hidden until feature is updated (SHOW_CLUB_PARTNERSHIPS_TAB) */}

        </div>
      </div>

      <SessionDetailsModal
        isOpen={showDetailsModal}
        sessionId={selectedSessionId}
        onClose={handleCloseSessionDetails}
        onSignup={handleSignupToSession}
        onCancelSignup={handleCancelSignup}
        currentUser={currentUser}
        userSignupStatus={null}
      />

      <CompletedSessionModal
        isOpen={showCompletedSessionModal}
        sessionId={selectedSessionId}
        onClose={handleCloseCompletedSession}
      />
    </div>
  );
};

export default ClubPage;
